import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { registerLocale } from "react-datepicker";
import { enGB, it, fr, es, de, ru, zhCN } from "date-fns/locale";
import dayjs from "dayjs";

import ProgressBar from "./components/ProgressBar";

import Step0 from "./components/steps/Step0";
import Step1 from "./components/steps/Step1";
import Step2 from "./components/steps/Step2";
import Step3 from "./components/steps/Step3";
import Step4 from "./components/steps/Step4";

import { setBaseUrl, setLanguage } from "./utils/api";
import { getTextLink, getTextNoLink, getFormattedDate } from "./utils/functions";

const App = ({ config }) => {
  const { t, i18n } = useTranslation();

  const [step, setStep] = useState(0);
  const [isReady, setIsReady] = useState(false);
  const [showDriverForm, setShowDriverForm] = useState(false);
  const [skipCustomerType, setSkipCustomerType] = useState(false);

  const [reservationData, setReservationData] = useState({});
  const [bookingData, setBookingData] = useState({ type: "", number: "" });
  const [customerData, setCustomerData] = useState({ type: "" });
  const [additionalDrivers, setAdditionalDrivers] = useState([]);
  const [privacyPreferences, setPrivacyPreferences] = useState({
    marketingConsent: true,
    profilingConsent: true,
    statisticsConsent: true
  });

  const handleBack = () => {
    const backStep = step - 1;

    if (backStep === 1) {
      if (customerData.type && !skipCustomerType) {
        setCustomerData((prevData) => ({
          ...prevData,
          type: "",
        }));
        return;
      }
    }
    else if (backStep === 2) {
      if (showDriverForm) {
        setShowDriverForm(false);
        return;
      }
    }
    setStep(backStep);
  };

  const handleStep = [
    () => setStep(1),
    ({ reservation, customer }) => {
      setReservationData(reservation);
      setCustomerData(customer);

      if (!customer.nation) {
        setCustomerData((prevData) => ({
          ...prevData,
          type: "individual",
          isDriver: true,
        }));
        setSkipCustomerType(true);
      }
      setStep(2);
    },
    () => setStep(3),
    () => setStep(4),
    () => {
      setBookingData({ type: "", number: "" });
      setCustomerData({ type: "" });
      setReservationData({});
      setAdditionalDrivers([]);
      setPrivacyPreferences({
        marketingConsent: true,
        profilingConsent: true,
        statisticsConsent: true
      });
      setStep(0);
    }
  ];

  useEffect(() => {
    // Set the default language in dayjs
    dayjs.locale(config.locale);

    // Register the supported languages in date-fns
    registerLocale("en", enGB);
    registerLocale("it", it);
    registerLocale("de", de);
    registerLocale("fr", fr);
    registerLocale("es", es);
    registerLocale("ru", ru);
    registerLocale("zh", zhCN);

    // Set the default language in i18n
    i18n.changeLanguage(config.locale);

    // Set the default language in the API
    setLanguage(config.locale);
    setBaseUrl(config.url);

    // Set initial values if in config
    if (config.tipo && config.codice && config.data_prenotazione) {
      setBookingData((prevData) => ({
        ...prevData,
        type: config.tipo || "",
        code: config.codice || "",
        date: getFormattedDate(config.data_prenotazione),
      }));
      setStep(1);
    }

    setIsReady(true);
  }, [config, i18n]);

  if (!isReady) return null;

  const [helperStart, helperEnd] = getTextNoLink(t("helper.text"));
  const helperLink = getTextLink(t("helper.text"));

  return (
    <div className="nol_checkin">
      {step > 0 && <ProgressBar totalSteps={4} currentStep={step} onBack={handleBack} />}
      {[
        <Step0
          bookingData={bookingData}
          setBookingData={setBookingData}
          onComplete={handleStep[step]}
        />,
        <Step1
          bookingData={bookingData}
          setBookingData={setBookingData}
          onComplete={handleStep[step]}
        />,
        <Step2
          customerData={customerData}
          setCustomerData={setCustomerData}
          onComplete={handleStep[step]}
        />,
        <Step3
          customerData={customerData}
          additionalDrivers={additionalDrivers}
          setAdditionalDrivers={setAdditionalDrivers}
          showDriverForm={showDriverForm}
          setShowDriverForm={setShowDriverForm}
          onComplete={handleStep[step]}
        />,
        <Step4
          checkinData={{
            reservationData,
            bookingData,
            customerData,
            additionalDrivers,
            privacyPreferences
          }}
          bookingData={bookingData}
          setBookingData={setBookingData}
          privacyPreferences={privacyPreferences}
          setPrivacyPreferences={setPrivacyPreferences}
          onComplete={handleStep[step]}
        />
      ][step]}
      <p className="helper">
        {helperStart}
        <a href={t("helper.link")} target="_blank" rel="noopener noreferrer">
          {helperLink}
        </a>
        {helperEnd}
      </p>
    </div>
  );
};

export default App;
